import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import {
  DynamicFormDefinition,
  DynamicFormLayout,
  DynamicImportMapping,
  DynamicImportResult,
  DynamicField,
} from '@models/dynamic-form.interface';

@Injectable({ providedIn: 'root' })
export class DynamicFormMockService {
  public getDefinition(): Observable<DynamicFormDefinition> {
    return of(this.buildDefinition()).pipe(delay(250));
  }

  public importFile(fileName: string, mappingId: string, definition: DynamicFormDefinition): Observable<DynamicImportResult> {
    const mapping = (definition.importMappings ?? []).find((m) => m.id === mappingId);
    if (!mapping) {
      return of({
        success: false,
        message: 'Import mapping was not found.',
        rowsProcessed: 1,
        rowsImported: 0,
        rowsFailed: 1,
        errors: [
          {
            rowNumber: 1,
            column: 'Mapping',
            value: mappingId,
            error: 'Selected mapping is invalid for this form.',
          },
        ],
      }).pipe(delay(250));
    }

    const importedData: Record<string, unknown> = {};
    for (const column of mapping.columnMappings) {
      const field = definition.fields.find((f) => f.id === column.fieldId);
      if (!field) {
        continue;
      }
      importedData[field.name] = this.createImportedValue(field, fileName);
    }

    return of({
      success: true,
      message: `Imported values from ${fileName}`,
      rowsProcessed: 1,
      rowsImported: 1,
      rowsFailed: 0,
      importedData,
      errors: [],
    }).pipe(delay(350));
  }

  private createImportedValue(field: DynamicField, fileName: string): unknown {
    switch (field.type) {
      case 'text':
        return `${field.label} from ${fileName}`;
      case 'number':
        return 12.345;
      case 'checkbox':
        return true;
      case 'date':
        return new Date().toISOString().slice(0, 10);
      case 'dropdown':
      case 'cascadingDropdown':
        return field.data?.[0]?.value ?? '';
      default:
        return '';
    }
  }

  private buildDefinition(): DynamicFormDefinition {
    const importMappings: DynamicImportMapping[] = [
      {
        id: 'map-fluid-tests',
        name: 'Fluid Test Upload',
        description: 'Maps common lab worksheet columns into Dynamic Form fields.',
        fileType: 'csv',
        columnMappings: [
          { columnName: 'Technician', fieldId: 'f-technician' },
          { columnName: 'SampleDensity', fieldId: 'f-density' },
          { columnName: 'SampleDate', fieldId: 'f-sample-date' },
          { columnName: 'Location', fieldId: 'f-location' },
        ],
      },
    ];

    const layout: DynamicFormLayout = {
      type: 'grid',
      tables: [
        {
          id: 'table-sample',
          title: 'Sample Attributes',
          description: 'Grid-based rendering mirrors the legacy table layout mode.',
          rows: 3,
          columns: 4,
          showHeaders: true,
          headers: [
            { text: 'Field', colSpan: 1 },
            { text: 'Value', colSpan: 1 },
            { text: 'Field', colSpan: 1 },
            { text: 'Value', colSpan: 1 },
          ],
          cells: [
            { row: 0, col: 0, cellType: 'fieldLabel', fieldId: 'f-technician' },
            { row: 0, col: 1, cellType: 'field', fieldId: 'f-technician' },
            { row: 0, col: 2, cellType: 'fieldLabel', fieldId: 'f-sample-date' },
            { row: 0, col: 3, cellType: 'field', fieldId: 'f-sample-date' },
            { row: 1, col: 0, cellType: 'fieldLabel', fieldId: 'f-location' },
            { row: 1, col: 1, cellType: 'field', fieldId: 'f-location' },
            { row: 1, col: 2, cellType: 'fieldLabel', fieldId: 'f-rig' },
            { row: 1, col: 3, cellType: 'field', fieldId: 'f-rig' },
            { row: 2, col: 0, cellType: 'staticText', text: 'Mud Density (ppg)' },
            { row: 2, col: 1, cellType: 'field', fieldId: 'f-density' },
            { row: 2, col: 2, cellType: 'calculation', expression: 'density * 0.1198' },
            { row: 2, col: 3, cellType: 'staticText', text: 'Converted SG shown after submit preview.' },
          ],
        },
      ],
    };

    return {
      id: 'df-001',
      name: 'Fluid Test Parameters',
      pageTitle: 'Dynamic Form',
      selectedMappingId: importMappings[0].id,
      fields: [
        { id: 'f-technician', name: 'technicianName', label: 'Technician Name', type: 'text', isRequired: true, minLength: 2 },
        { id: 'f-sample-date', name: 'sampleDate', label: 'Sample Date', type: 'date', isRequired: true },
        { id: 'f-location', name: 'location', label: 'Location', type: 'dropdown', isRequired: true, data: [
          { value: 'onshore', text: 'Onshore' },
          { value: 'offshore', text: 'Offshore' },
        ] },
        {
          id: 'f-rig',
          name: 'rig',
          label: 'Rig',
          type: 'cascadingDropdown',
          parentFieldId: 'f-location',
          data: [],
          cascadingData: {
            onshore: [
              { value: 'rig-101', text: 'Rig 101' },
              { value: 'rig-220', text: 'Rig 220' },
            ],
            offshore: [
              { value: 'rig-501', text: 'Rig 501' },
              { value: 'rig-614', text: 'Rig 614' },
            ],
          },
        },
        {
          id: 'f-density',
          name: 'density',
          label: 'Density',
          type: 'number',
          isRequired: true,
          allowDecimal: true,
          decimalPlaces: 3,
          minValue: 5,
          maxValue: 25,
        },
        { id: 'f-retest', name: 'requiresRetest', label: 'Requires Retest', type: 'checkbox', defaultValue: false },
        { id: 'f-comments', name: 'comments', label: 'Comments', type: 'text', minLength: 0, maxLength: 250 },
      ],
      layout,
      importMappings,
    };
  }
}
